import React, { useRef, useState } from 'react';
import Link from 'next/link';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import isEmptyish from '@tmap/mmm-core/isEmptyish';
import { ChevronRight } from '@tmap/mmm-style-guide/src/Icon';
import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { Chip } from '@tmap/mmm-style-guide/src/Chip';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { styled } from '@tmap/mmm-style-guide/src/styled';

import EligibilityChecker from './eligibilityChecker';
import BlockContent from '../block-content';
import GalleryCommunityCard from '../communityCard/gallery';
import HorizontalCommunityCard from '../communityCard/horizontal';

import isCommonAppIncentive from '../../lib/incentive/isCommonAppIncentive';
import { offerTypeFromValue } from '../../lib/filterTypes';

const DescriptionHeading = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(6),
  paddingBottom: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
}));

function OfferBodyCommunities(props) {
  const { communities = [] } = props;
  const swiperRef = useRef(null);
  if (!communities?.length) {
    return null;
  }
  return (
    <Box>
      <DescriptionHeading variant='body2'>Participating Communities</DescriptionHeading>
      <Grid container sx={{ display: { xs: 'flex', md: 'none' }, marginTop: 1 }}>
        <Swiper ref={swiperRef} slidesPerView={communities.length < 2 ? 1 : 'auto'} spaceBetween={16} modules={[Navigation]}>
          {communities.map((community, i) => (
            <SwiperSlide key={community._id} style={{ width: 'min(328px, 75vw)' }}>
              <GalleryCommunityCard
                community={community}
                hideApplyLink
                shouldLazyLoad
                lazyProps={{ offset: 0, height: 186 }}
                placementId={`offerParentCommunities-${i}`}
                pageId='offer'
                disableSwiper
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </Grid>
      <Grid container spacing={2} sx={{ display: { xs: 'none', md: 'flex' } }}>
        {communities.map((community, i) => (
          <Grid item xs={12} key={community._id}>
            <HorizontalCommunityCard
              hideApplyLink
              community={community}
              shouldLazyLoad
              lazyProps={{ offset: 0, height: 186 }}
              placementId={`offerParentCommunities-${i}`}
              pageId='offer'
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function OfferBody(props) {
  const [showAllFAQs, setShowAllFAQs] = useState(false);
  const { incentive } = props;
  const faqs = (incentive.faqs || []).filter((faq) => faq.question && faq.answer);

  const showEligibilityWidget = incentive.status !== 'inactive' && isCommonAppIncentive(incentive);
  const hasIntro = !isEmptyish(incentive.eligibilityIntro);
  const showEligibility = Boolean(
    incentive.eligibilityRules && incentive.eligibilityRules.length > 0,
  );
  return (
    <>
      <DescriptionHeading variant='body2'>Description</DescriptionHeading>
      {!isEmptyish(incentive.longDescription) && (
        <Typography variant='body1' color='textSecondary' sx={{ whiteSpace: 'pre-line' }}>
          {incentive.longDescription}
        </Typography>
      )}
      {incentive.body && (
        <Typography variant='body1' component='div' color='textSecondary' sx={{ whiteSpace: 'pre-line', marginTop: 1 }}>
          <BlockContent value={incentive.body} nested />
        </Typography>
      )}

      <OfferBodyCommunities communities={incentive.communities} />

      {(showEligibility || showEligibilityWidget) && (
        <>
          <DescriptionHeading variant='body2'>Eligibility</DescriptionHeading>
          {showEligibilityWidget && <EligibilityChecker incentive={incentive} />}
          {showEligibility && (
            <>
              <Typography variant='subtitle' fontWeight={500}>Eligibility Requirements</Typography>
              {hasIntro && <Typography variant='body1' color='textSecondary' sx={{ mt: 2 }}>{incentive.eligibilityIntro}</Typography>}
              <ul style={hasIntro ? { marginTop: '0px' } : {}}>
                {incentive.eligibilityRules.map((rule, i) => (
                  <li key={i}>
                    <Typography variant='body1' color='textSecondary'>{rule}</Typography>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {faqs.length > 0 && (
        <>
          <DescriptionHeading variant='body2'>FAQ</DescriptionHeading>
          <Grid container spacing={3}>
            {faqs.slice(0, showAllFAQs ? undefined : 3).map((faq) => (
              <Grid item xs={12} key={faq._id || faq._key}>
                <Typography component='p' variant='subtitle' fontWeight={500} sx={{ marginBottom: 1 }}>{faq.question}</Typography>
                <Typography variant='body2'>{faq.answer}</Typography>
                {faq.infoText && faq.infoLink && (
                  <Link href={faq.infoLink} target='_blank'>
                    <Button
                      color='info'
                      sx={{
                        marginTop: 0.25,
                        marginLeft: -0.5,
                        marginBottom: -0.5,
                        display: 'inline-block',
                      }}
                    >
                      {faq.infoText}
                    </Button>
                  </Link>
                )}
              </Grid>
            ))}
            {!showAllFAQs && faqs.length > 3 && (
              <Grid item xs={12}>
                <Button color='inverted' onClick={() => setShowAllFAQs(true)}><ChevronRight />&nbsp;View All {faqs.length} FAQs</Button>
              </Grid>
            )}
          </Grid>
        </>
      )}

      {incentive.values && (
        <>
          <DescriptionHeading variant='body2'>Other Details</DescriptionHeading>
          <Typography component='p' variant='subtitle' fontWeight={500} sx={{ marginBottom: 2 }}>Program Categories</Typography>
          <Grid container spacing={1}>
            {Array.from(new Set(incentive.values.map((value) => value.valueType)))
              .map(offerTypeFromValue).map((offerType, i) => (
                <Grid item key={i}>
                  <Link href={`/get-paid/?ot=${offerType.value}`}>
                    <Chip
                      label={offerType.title}
                      onClick={() => { }}
                      aria-label={`Browse programs with ${offerType.title} incentives`}
                    />
                  </Link>
                </Grid>
              ))}
          </Grid>
        </>
      )}
    </>
  );
}

export default OfferBody;
